// Helpers for generating and using idempotency keys.
//
// Goal: when a user retries an action (snapshot a guide, publish to library,
// etc.) — whether because they double-clicked, a network blip retried the
// fetch, or React re-rendered an effect — the SECOND call must NOT create a
// duplicate row. The server-side guarantee lives in two partial unique
// indexes (see migration) plus the `upsert_*_idempotent` RPCs. The client
// just has to hand the server a STABLE key derived from the same inputs.
//
// We use SHA-256 over a canonicalised string. Web Crypto is available in
// every browser we ship to + Deno edge runtime, so no polyfill needed.

/** Canonicalise an arbitrary input into a stable string for hashing. */
function canon(input: unknown): string {
  if (input === null || input === undefined) return "";
  if (typeof input === "string") return input;
  if (typeof input === "number" || typeof input === "boolean") return String(input);
  // For objects / arrays, sort keys so {a:1,b:2} === {b:2,a:1}.
  return JSON.stringify(input, Object.keys(input as object).sort());
}

/**
 * Build a deterministic SHA-256 idempotency key from one or more parts.
 * The same inputs always produce the same 64-char hex string, so the
 * server-side unique index turns retries into no-ops.
 */
export async function buildIdempotencyKey(...parts: unknown[]): Promise<string> {
  const payload = parts.map(canon).join("|");
  const bytes = new TextEncoder().encode(payload);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}
